import { useCallback, useMemo } from 'react';

import type { DxfLayer } from '@frontend/api';

import type { DrawingModel, DxfViewerSession } from '../session/types';
import { useReadableStore } from '../session/useSessionState';

export type DxfLayerPanelState = {
  layers: readonly DxfLayer[];
  hiddenLayers: ReadonlySet<number>;
  counts: ReadonlyMap<number, number>;
  selectableLayers: ReadonlySet<number>;
  indeterminateLayers: ReadonlySet<number>;
  onToggleLayer: (layerIndex: number, nextVisible: boolean) => void;
  onSelectLayer: (layerIndex: number, isAdditive: boolean) => void;
  onFrameLayer: (layerIndex: number) => void;
  onHoverLayer: (layerIndex: number | null) => void;
  isLayerColoringOn: boolean;
  onToggleLayerColoring: () => void;
};

/**
 * Connects `DxfLayerPanel` to the session. Call only under a ready drawing.
 */
export const useDxfLayerPanel = (
  session: DxfViewerSession,
  model: DrawingModel,
): DxfLayerPanelState => {
  const hiddenLayers = useReadableStore(
    session.hiddenLayers,
    (current) => current,
  );
  const isLayerColoringOn = useReadableStore(
    session.overlays,
    (current) => current.layerColoring,
  );
  const isPmiVisible = useReadableStore(
    session.overlays,
    (current) => current.pmi,
  );

  // A hidden layer that still carries visible PMI is neither on nor off, and the
  // checkbox has to say so rather than claiming the layer is fully hidden.
  const indeterminateLayers = useMemo(() => {
    if (!isPmiVisible) {
      return new Set<number>();
    }
    const partial = new Set<number>();
    hiddenLayers.forEach((layerIndex) => {
      if (model.layersWithPmi.has(layerIndex)) {
        partial.add(layerIndex);
      }
    });
    return partial;
  }, [isPmiVisible, model, hiddenLayers]);

  // A layer with no stitched geometry has nothing to select, so its name stays
  // inert rather than looking clickable and doing nothing.
  const selectableLayers = useMemo(() => {
    const withGeometry = new Set<number>();
    model.loops.forEach((loop) =>
      loop.layers.forEach((layer) => withGeometry.add(layer)),
    );
    return withGeometry;
  }, [model]);

  const onToggleLayer = useCallback(
    (layerIndex: number, nextVisible: boolean) => {
      session.layers.setVisible(layerIndex, nextVisible);
    },
    [session],
  );

  const onSelectLayer = useCallback(
    (layerIndex: number, isAdditive: boolean) => {
      session.layers.select(layerIndex, isAdditive ? 'add' : 'replace');
    },
    [session],
  );

  const onFrameLayer = useCallback(
    (layerIndex: number) => session.layers.frame(layerIndex),
    [session],
  );

  const onHoverLayer = useCallback(
    (layerIndex: number | null) => session.layers.setHovered(layerIndex),
    [session],
  );

  const onToggleLayerColoring = useCallback(
    () => session.overlays.toggle('layerColoring'),
    [session],
  );

  return {
    layers: model.scene.layers,
    hiddenLayers,
    counts: model.entityCountsByLayer,
    selectableLayers,
    indeterminateLayers,
    onToggleLayer,
    onSelectLayer,
    onFrameLayer,
    onHoverLayer,
    isLayerColoringOn,
    onToggleLayerColoring,
  };
};
